import { useEffect } from "react";
import dynamic from "next/dynamic";
import { handlePlayPauseInDOM } from "redux-share/actions";
import { useAppDispatch, useAppSelector } from "redux-share/hooks";
import { setIsComplexPortrait } from "redux-share/slices/videoDataSlice";
import { setIsAutoPlay, setIsClickInnerCard } from "redux-share/slices/controllerSlice";
import { ConditionName, IParams, zIndex } from "interfaces";
import useHandleParams from "helpers/hooks/useHandleParams";
import useHandleKeyPress from "helpers/hooks/useHandleKeyPress";
import { clearRedux, conditions } from "helpers";
import { shallowEqual } from "react-redux";
import ToastContainer from "mobin-toast";
import ErrorBoundary from "./errorBoundary";

const StaticPlayer = dynamic(() => import("./players/staticPlayer/player"));
const LiveAndVodPlayer = dynamic(() => import("./players/liveAndVodPlayer/player"));
const SplusPlayer = dynamic(() => import("../splus/splusPlayer"));
const ParamsErrorPopover = dynamic(() => import("../../player-components/paramsErrorPopover/paramsErrorPopover"));

const VideoPlayer = ({ params }: { params: IParams }) => {
    const dispatch = useAppDispatch();

    useAppSelector(({ controller: { isFullScreen } }) => ({ isFullScreen }), shallowEqual);
    useAppSelector(({ videoData: { isReady, params, isComplexPortrait } }) => ({ isReady, params, isComplexPortrait }), shallowEqual);

    const { requiredsItems, notRequiredsItems } = useHandleParams(params);
    useHandleKeyPress();

    useEffect(() => {
        dispatch(setIsClickInnerCard(false));
        return () => clearRedux();
    }, []);

    useEffect(() => {
        const isAutoPlay = localStorage.getItem("isAutoPlay");
        if (isAutoPlay) dispatch(setIsAutoPlay(isAutoPlay === "true"));
        (Document.prototype as any).playPause = (type: boolean) => handlePlayPauseInDOM(type);
        if (!conditions(ConditionName.isIOS)) {
            window.screen.orientation.addEventListener("change", (event: any) => {
                if (!conditions(ConditionName.isFullScreen) && conditions(ConditionName.isParamsPortrait)) dispatch(setIsComplexPortrait(true));
                const videoPlayerElement = document.getElementById("video-player-module");
                if (!!videoPlayerElement) {
                    if (event.target.type.includes("landscape") && !conditions(ConditionName.isFullScreen)) {
                        videoPlayerElement.classList.add("pl-overflow-scroll");
                        videoPlayerElement.classList.remove("pl-overflow-hidden");
                    } else {
                        videoPlayerElement.classList.add("pl-overflow-hidden");
                        videoPlayerElement.classList.remove("pl-overflow-scroll");
                    }
                }
            });
        }
    }, []);

    const checkOverflow = () => {
        let overflow: string = "";
        if (!conditions(ConditionName.isParamsPortrait)) overflow = "pl-overflow-hidden";
        else if (window.innerWidth > window.innerHeight && conditions(ConditionName.isFullScreen)) overflow = "pl-overflow-hidden";
        else if (window.innerWidth > window.innerHeight && !conditions(ConditionName.isFullScreen)) overflow = "pl-overflow-auto";
        else if (window.innerWidth < window.innerHeight) overflow = "pl-overflow-hidden";
        return overflow;
    };

    let element = <></>;

    if (requiredsItems.length || notRequiredsItems.length || !conditions(ConditionName.hasAllParams))
        element = <ParamsErrorPopover requiredsItems={requiredsItems} notRequiredsItems={notRequiredsItems} />;
    else if (conditions(ConditionName.isReady)) {
        element = (
            <div
                className={`${checkOverflow()} ${conditions(ConditionName.isComplexPortrait) && "pl-max-h-[100dvh] !no-scrollbar"} ${
                    conditions(ConditionName.isFullScreen) && "!pl-overflow-hidden pl-fixed pl-top-0 pl-right-0 pl-w-screen pl-h-screen"
                }`}
                style={{ zIndex: zIndex.videoPlayerInFullScreen, touchAction: "pan-y" }}
                id="video-player-module"
            >
                {conditions(ConditionName.isStaticFormat) ? (
                    <StaticPlayer />
                ) : conditions(ConditionName.isSplusEnvironment) ? (
                    <SplusPlayer />
                ) : (
                    <LiveAndVodPlayer />
                )}
            </div>
        );
    } else element = <div className="pl-w-full !pl-aspect-video pl-bg-white dark:pl-bg-black pl-overflow-hidden pl-max-h-screen" />;

    return (
        <ErrorBoundary>
            <ToastContainer position="bottom-right" autoClose={3000} theme={conditions(ConditionName.isDarkMode) ? "dark" : "light"} />
            {element}
        </ErrorBoundary>
    );
};

export default VideoPlayer;
